import React from 'react';
import Icon from '../../../components/AppIcon';

const SEOContentSection = () => {
  return (
    <div className="mt-16 pt-12 border-t border-border">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column: Educational Content */}
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
              How to Estimate RV Park Profitability
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Calculating the potential income of an RV park requires analyzing multiple revenue streams and operational costs. 
              Our <strong>RV Park Income Calculator</strong> simplifies this process by accounting for critical factors like 
              daily/monthly rates, occupancy seasonality, and ancillary income from utilities and amenities.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Whether you're developing raw land or acquiring an existing campground, understanding your 
              <strong> Net Operating Income (NOI)</strong> is essential for securing financing and ensuring long-term ROI.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
              Key Factors Influencing RV Park Revenue
            </h3>
            <ul className="space-y-3">
              {[
                {
                  title: "Location & Demand",
                  desc: "Proximity to national parks, highways, and attractions significantly impacts nightly rates."
                },
                {
                  title: "Site Mix Strategy",
                  desc: "Balancing short-term (transient) vs. long-term (seasonal) sites optimizes occupancy year-round."
                },
                {
                  title: "Amenity Multipliers",
                  desc: "Premium features like 50-amp service, Wi-Fi, and pull-through sites command 15-30% higher rates."
                }
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="mt-1 bg-primary/10 p-1 rounded">
                    <Icon name="Check" size={14} color="var(--color-primary)" />
                  </div>
                  <div>
                    <span className="font-medium text-foreground block">{item.title}</span>
                    <span className="text-sm text-muted-foreground">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Right Column: FAQ & Guide */}
        <div className="space-y-8">
          <section className="bg-muted/30 rounded-xl p-6 border border-border/50">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-foreground">What is a good profit margin for an RV park?</h4>
                <p className="text-sm text-muted-foreground">
                  Successful RV parks typically operate with profit margins between <strong>35% and 50%</strong>. 
                  Efficiency varies based on management style, utility costs, and the ratio of long-term vs. nightly guests.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-foreground">How do I calculate Cap Rate?</h4>
                <p className="text-sm text-muted-foreground">
                  Capitalization Rate (Cap Rate) is calculated by dividing Net Operating Income (NOI) by the 
                  Current Market Value (or Purchase Price) of the asset.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
              Why Use This Calculator?
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              Unlike generic real estate spreadsheets, this tool is calibrated specifically for the outdoor hospitality industry.
              It uses real-time logic to adjust for:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card p-3 rounded-lg border border-border text-center">
                <span className="block font-bold text-primary text-lg">Seasonal</span>
                <span className="text-xs text-muted-foreground">Occupancy Shifts</span>
              </div>
              <div className="bg-card p-3 rounded-lg border border-border text-center">
                <span className="block font-bold text-primary text-lg">Utility</span>
                <span className="text-xs text-muted-foreground">Cost Variable</span>
              </div>
              <div className="bg-card p-3 rounded-lg border border-border text-center">
                <span className="block font-bold text-primary text-lg">Ancillary</span>
                <span className="text-xs text-muted-foreground">Revenue Streams</span>
              </div>
              <div className="bg-card p-3 rounded-lg border border-border text-center">
                <span className="block font-bold text-primary text-lg">Growth</span>
                <span className="text-xs text-muted-foreground">5-Year Trends</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SEOContentSection;
